import React, { useEffect, useMemo, useRef } from 'react'
import { Alert, Button, Card, Space, Typography } from 'antd'
import { useHistory } from 'react-router-dom'
import { useIntl } from 'react-intl'

import { toPartDayOptionId } from '@vacationtracker/shared/functions/part-day-option-id'
import IntlMessages from '../../util/IntlMessages'
import { VeeMessage } from './vee-message'
import { VeeWorking } from './vee-working'
import { getFollowUpKeys } from './follow-ups'
import { VeeSendOutcome } from './hooks'
import { IVeeMessageItem, IVeeMessageMetadata, parseVeeMetadata } from './types'

interface IDraftFact {
  labelKey: string
  value: string
}

type DateFormatter = (date: string) => string

function formatFactRange(formatDate: DateFormatter, startDate?: unknown, endDate?: unknown): string | undefined {
  if (typeof startDate !== 'string' || typeof endDate !== 'string' || !startDate || !endDate) {
    return undefined
  }
  return startDate === endDate ? formatDate(startDate) : `${formatDate(startDate)} - ${formatDate(endDate)}`
}

interface ISuggestionAction {
  start_date: string
  end_date: string
  leave_type_id?: string
  is_best?: boolean
}

const MAX_SUGGESTION_ACTIONS = 4

const DraftFacts: React.FC<{ facts: IDraftFact[] }> = ({ facts }) => {
  if (facts.length === 0) {
    return null
  }
  return (
    <div className="vee-draft-facts">
      {facts.map((fact, index) => (
        <div key={`${fact.labelKey}-${index}`} className="vee-draft-fact">
          <Typography.Text type="secondary">
            <IntlMessages id={fact.labelKey} />:
          </Typography.Text>
          <Typography.Text>{fact.value}</Typography.Text>
        </div>
      ))}
    </div>
  )
}

// Suggestions carry full ISO timestamps (toISOString); previews carry plain yyyy-MM-dd.
// Normalising to the calendar-date part keeps the label formatter and the form prefill
// timezone-safe either way.
function toDateOnly(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  return value.match(/^\d{4}-\d{2}-\d{2}/)?.[0]
}

function extractSuggestionActions(metadata: IVeeMessageMetadata | undefined): ISuggestionAction[] {
  return (metadata?.toolCalls || [])
    .filter((toolCall) => toolCall.toolName === 'get_leave_suggestions')
    .flatMap((toolCall) => (toolCall.toolResult?.data?.suggestions as ISuggestionAction[] | undefined) || [])
    .flatMap((suggestion) => {
      const startDate = toDateOnly(suggestion.start_date)
      const endDate = toDateOnly(suggestion.end_date)
      return startDate && endDate ? [{ ...suggestion, start_date: startDate, end_date: endDate }] : []
    })
    .slice(0, MAX_SUGGESTION_ACTIONS)
}

function buildDraftFacts(previews: IVeeMessageMetadata['previews'], formatDate: DateFormatter, partialDayLabel: string): IDraftFact[] {
  const facts: IDraftFact[] = []
  for (const preview of previews || []) {
    if (preview.toolName === 'request_leave') {
      const leaveRequest = preview.data?.leave_request as Record<string, unknown> | undefined
      const dates = formatFactRange(formatDate, leaveRequest?.start_date, leaveRequest?.end_date)
      if (leaveRequest && dates) {
        if (typeof leaveRequest.leave_type === 'string' && leaveRequest.leave_type) {
          facts.push({ labelKey: 'bot.agent.field.leaveType', value: leaveRequest.leave_type })
        }
        facts.push({
          labelKey: 'bot.agent.field.dates',
          value: leaveRequest.is_part_day === true ? `${dates} ${partialDayLabel}` : dates,
        })
        if (typeof leaveRequest.reason === 'string' && leaveRequest.reason && leaveRequest.reason !== '(none)') {
          facts.push({ labelKey: 'bot.agent.field.reason', value: leaveRequest.reason })
        }
      }
    }
    if (preview.toolName === 'edit_leave') {
      const changes = preview.data?.changes as { before?: Record<string, unknown>; after?: Record<string, unknown> } | undefined
      const before = formatFactRange(formatDate, changes?.before?.start_date, changes?.before?.end_date)
      const after = formatFactRange(formatDate, changes?.after?.start_date, changes?.after?.end_date)
      if (before && after) {
        facts.push({ labelKey: 'bot.agent.field.currentDates', value: before })
        facts.push({ labelKey: 'bot.agent.field.newDates', value: after })
      }
    }
    if (preview.toolName === 'cancel_leave') {
      const leave = preview.data?.leave_to_cancel as Record<string, unknown> | undefined
      const dates = formatFactRange(formatDate, leave?.start_date, leave?.end_date)
      if (dates) {
        facts.push({ labelKey: 'bot.agent.field.dates', value: dates })
        if (leave?.status) {
          facts.push({ labelKey: 'bot.agent.field.status', value: String(leave.status).toUpperCase() })
        }
      }
    }
  }
  return facts
}

// Error markers carry fallback text (so backend history stays valid) but render as an Alert, not a bubble.
function isHiddenErrorMarker(message: IVeeMessageItem): boolean {
  if (message.role !== 'assistant') {
    return false
  }
  return !message.content || Boolean(parseVeeMetadata(message.metadata)?.error)
}

export function buildRequestLeaveUrl(toolInput: Record<string, unknown> | undefined): string {
  const params = new URLSearchParams()
  const fields: Array<[string, string]> = [
    ['startDate', 'start_date'],
    ['endDate', 'end_date'],
    ['leaveTypeId', 'leave_type_id'],
    ['reason', 'reason'],
  ]
  for (const [param, field] of fields) {
    let value = toolInput?.[field]
    // The leave form models part-day as a separate '<id>|part-day' option, so the flag
    // travels inside the leaveTypeId param - there is no standalone part-day param.
    if (field === 'leave_type_id' && typeof value === 'string' && value && toolInput?.is_part_day === true) {
      value = toPartDayOptionId(value)
    }
    if (typeof value === 'string' && value) {
      params.append(param, value)
    }
  }
  const query = params.toString()
  return query ? `/app/leaves/request-leave?${query}` : '/app/leaves/request-leave'
}

interface IVeeConversationProps {
  messages: IVeeMessageItem[]
  waiting: boolean
  pendingUserMessage: string | null
  timedOut: boolean
  sendFailed: boolean
  limitReached: number | null
  onSend: (message: string, options?: { confirmationToken?: string }) => Promise<VeeSendOutcome>
  onRetry: () => Promise<VeeSendOutcome>
}

export const VeeConversation: React.FC<IVeeConversationProps> = ({ messages, waiting, pendingUserMessage, timedOut, sendFailed, limitReached, onSend, onRetry }) => {
  const { formatMessage, formatDate } = useIntl()
  const history = useHistory()
  const scrollRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [messages.length, waiting, timedOut, sendFailed, limitReached])

  const lastMessage = messages[messages.length - 1]
  // Parsed once per data change - the panel re-renders every poll tick while waiting.
  const { visibleMessages, lastMetadata } = useMemo(
    () => ({
      visibleMessages: messages.filter((message) => !isHiddenErrorMarker(message)),
      lastMetadata: lastMessage?.role === 'assistant' ? parseVeeMetadata(lastMessage.metadata) : undefined,
    }),
    [messages, lastMessage]
  )
  const isLastMessageCurrent = !waiting && !timedOut

  // Confirmations survive an error or timeout (the worker re-mints the token), so the
  // draft card only hides while a send is actually in flight.
  const pendingConfirmations = (!waiting && lastMetadata?.pendingConfirmations) || []
  const tokenConfirmations = pendingConfirmations.filter((confirmation) => !confirmation.presentsNativeForm)
  const formConfirmation = tokenConfirmations.length === 0 ? pendingConfirmations.find((confirmation) => confirmation.presentsNativeForm) : undefined
  const hasError = Boolean(isLastMessageCurrent && lastMetadata?.error)

  const shortDate: DateFormatter = (date) => formatDate(new Date(`${date}T00:00:00`), { year: 'numeric', month: 'short', day: 'numeric' })

  const showLastTurnActions = isLastMessageCurrent && Boolean(lastMessage) && pendingConfirmations.length === 0 && !hasError
  const suggestionActions = showLastTurnActions ? extractSuggestionActions(lastMetadata) : []
  // Suggestion buttons open the prefilled form directly - model-offered date chips for the
  // same weeks would be a redundant, weaker duplicate row.
  const optionChips = showLastTurnActions && suggestionActions.length === 0 ? lastMetadata?.options || [] : []
  // Option chips are a direct answer to the model's question - follow-up openers would compete,
  // so one row shows either the options or the follow-ups, never both.
  const quickRepliesAreOptions = optionChips.length > 0
  const quickReplies = quickRepliesAreOptions
    ? optionChips
    : showLastTurnActions
      ? getFollowUpKeys((lastMetadata?.toolCalls || []).map((toolCall) => toolCall.toolName)).map((key) => formatMessage({ id: key }))
      : []

  const draftFacts = tokenConfirmations.length > 0 || formConfirmation ? buildDraftFacts(lastMetadata?.previews, shortDate, formatMessage({ id: 'bot.agent.partialDay' })) : []

  return (
    <div className="vee-conversation" ref={scrollRef} role="log" aria-live="polite" tabIndex={0}>
      {visibleMessages.map((message) => (
        <VeeMessage key={`${message.timestamp}-${message.role}`} message={message} />
      ))}

      {pendingUserMessage && <VeeMessage message={{ id: 'pending', role: 'user', content: pendingUserMessage, timestamp: '', type: 'CONVERSATION_MESSAGES' }} />}

      {waiting && <VeeWorking />}

      {tokenConfirmations.length > 0 && lastMetadata?.confirmationToken && (
        <Card size="small" className="vee-draft-card" title={formatMessage({ id: 'veeAssistant.draftTitle' })}>
          <DraftFacts facts={draftFacts} />
          <Typography.Text type="secondary">
            <IntlMessages id="veeAssistant.draftPreviewNote" />
          </Typography.Text>
          <Space className="vee-draft-actions">
            {/* The canned phrases are what the model reads, so they stay English (like all
                model-facing text); the button labels are the translated UI. Only Confirm
                carries the single-use token - without it the worker never executes. */}
            <Button type="primary" onClick={() => void onSend('Yes, confirm.', { confirmationToken: lastMetadata.confirmationToken })}>
              <IntlMessages id="veeAssistant.draftConfirm" />
            </Button>
            <Button onClick={() => void onSend('No, discard that. Do not proceed.')}>
              <IntlMessages id="veeAssistant.draftDiscard" />
            </Button>
          </Space>
        </Card>
      )}

      {formConfirmation && (
        <Card size="small" className="vee-draft-card">
          <DraftFacts facts={draftFacts} />
          <Typography.Text type="secondary">
            <IntlMessages id="veeAssistant.formOpenedNote" />
          </Typography.Text>
          <Space className="vee-draft-actions">
            <Button type="primary" onClick={() => history.push(buildRequestLeaveUrl(formConfirmation.toolInput))}>
              <IntlMessages id="veeAssistant.openLeaveForm" />
            </Button>
          </Space>
        </Card>
      )}

      {suggestionActions.length > 0 && (
        <div className="vee-suggestion-actions">
          {suggestionActions.map((suggestion) => (
            <Button
              key={`${suggestion.start_date}-${suggestion.end_date}`}
              size="small"
              onClick={() =>
                history.push(
                  buildRequestLeaveUrl({
                    leave_type_id: suggestion.leave_type_id,
                    start_date: suggestion.start_date,
                    end_date: suggestion.end_date,
                  })
                )
              }
            >
              {formatMessage({ id: 'veeAssistant.suggestionOpenForm' }, { dates: formatFactRange(shortDate, suggestion.start_date, suggestion.end_date) })}
              {suggestion.is_best ? ` · ${formatMessage({ id: 'veeAssistant.suggestionBest' })}` : ''}
            </Button>
          ))}
        </div>
      )}

      {quickReplies.length > 0 && (
        <div className="vee-follow-ups">
          {quickReplies.map((phrase) => (
            <Button
              key={phrase}
              size="small"
              shape="round"
              type={quickRepliesAreOptions ? 'primary' : 'default'}
              ghost={quickRepliesAreOptions}
              onClick={() => void onSend(phrase)}
            >
              {phrase}
            </Button>
          ))}
        </div>
      )}

      {(timedOut || hasError) && (
        <Alert
          type="error"
          message={formatMessage({ id: 'veeAssistant.errorTitle' })}
          description={formatMessage({ id: 'veeAssistant.errorInfo' })}
          showIcon
          action={
            timedOut ? (
              <Button size="small" onClick={() => void onRetry()}>
                <IntlMessages id="veeAssistant.tryAgain" />
              </Button>
            ) : undefined
          }
        />
      )}

      {sendFailed && <Alert type="error" message={formatMessage({ id: 'veeAssistant.sendError' })} showIcon />}

      {limitReached !== null && <Alert type="warning" message={formatMessage({ id: 'bot.agent.dailyLimitReached' }, { limit: limitReached })} showIcon />}
    </div>
  )
}
